//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

export interface UnifiedCardData {
  id: string;
  type: 'Issues' | 'Builds' | 'Pull Requests' | 'Commits' | 'Deployments';
  cardTitle: string;
  created: number;
  owner: string;
  loopName: string;
  description?: string;
  url: string;
  status?: string;
  issueType?: string;
  commitUrls?: string[];
  pullRequestUrls?: string[];
  buildUrls?: string[];

  // Commit-specific fields
  environment?: string;
  application?: string;
  result?: string;
  source?: string;
  repositoryType?: string;
  repository?: string;

  // Pull Request-specific fields
  createdBy?: string;
  branch?: string;
  // Standardized naming for base branch
  baseBranch?: string;
  repositoryName?: string;
  repositoryOwner?: string;

  // Deployment-specific fields
  triggeredBy?: string;
  version?: string;
  labels?: string;
  startTime?: string;
  endTime?: string;

  // Build-specific fields
  requestor?: string;
  loop?: string;
  commits?: string;
  sourceBranch?: string;
  branchName?: string;
  labelsList?: string;
  buildSource?: string;
  buildBranch?: string | null;
  buildLabel?: string | null;
}

// Generic UI fallback helper: returns translated 'not-available' if value is null/undefined/empty
export const displayOrNA = (value: any, t: (key: string) => string): string => {
  if (value === null || value === undefined) return t('not-available');
  if (typeof value === 'string' && value.trim() === '')
    return t('not-available');
  return String(value);
};

// Shared date formatting utility for card components.
// Returns DD/MM/YYYY or '--' if timestamp is falsy.
export const formatDate = (timestamp?: number) => {
  if (!timestamp) return '--';
  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '--';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return '--';
  }
};

// Chip color mapping utilities
// Returns Tailwind CSS classes for status/result badge styling.

const SUCCESS_RE = /(success|passed|ok|complete|completed|active|green)/i;
const ERROR_RE = /(fail|failed|error|broken|red)/i;
const WARNING_RE = /(warn|warning|yellow)/i;
const INFO_RE = /(info|pending|queued|queued up)/i;
const RUNNING_RE = /(running|in progress|progress|submitted|executing)/i;

export function chipColorForStatus(status?: string): string {
  if (!status)
    return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  const s = status.trim().toLowerCase();
  if (ERROR_RE.test(s))
    return 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300';
  if (SUCCESS_RE.test(s))
    return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300';
  if (WARNING_RE.test(s))
    return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300';
  if (INFO_RE.test(s))
    return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300';
  if (RUNNING_RE.test(s))
    return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300';
  return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
}

export function chipColorForResult(result?: string): string {
  return chipColorForStatus(result);
}
